/**
 * Hook for loading reference attributes for RequestApprovalType entities
 *
 * This hook handles:
 * 1. Loading all reference attributes (for PossessiveTargetAttribute dropdown) from tier2 entitlements
 *    (GET /mgmt/Entitlements/entity/requestapprovaltype) when schema API is disabled
 * 2. Loading target entity type reference attributes based on selected PossessiveTargetAttribute
 * 3. Caching and deduplication of API calls
 * 4. Error handling and retry prevention
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { ScimSchemaService } from '../services/ScimSchemaService';
import EntitlementsService from '../services/EntitlementsService';
import { store } from '../store/store';

const REQUEST_APPROVAL_TYPE_ENTITY_ID = 'requestapprovaltype';

/** Use attribute id as-is (no stripping). */
function tier2AttrIdToName(id: string): string {
    return (id ?? '').toString().trim();
}

export interface ReferenceAttributeInfo {
    name: string;
    isReference: boolean;
}

export interface ReferenceAttribute {
    name: string;
    id: string;
    value: string;
    label: string;
    type: string;
    required: boolean;
    multiValued: boolean;
    isReference: boolean;
    entities?: string[];
    isReferenceTo?: any[];
}

export interface UseReferenceAttributeLoaderOptions {
    isRequestApprovalType: boolean;
    open: boolean;
    possessiveTargetAttribute: any;
    onActorsAttributeUpdate?: (value: string) => void;
    existingActorsValue?: any;
}

export interface UseReferenceAttributeLoaderResult {
    // All reference attributes (for PossessiveTargetAttribute dropdown)
    allReferenceAttributes: ReferenceAttribute[];
    loadingAllReferenceAttributes: boolean;

    // Target entity type reference attributes (for PossessiveActorsAttribute dropdown)
    targetEntityTypeReferenceAttributes: any[];
    loadingReferenceAttributes: boolean;

    // Selected attribute info for display
    selectedReferenceAttributeInfo: ReferenceAttributeInfo | null;

    // Ref to track last fetched attribute (for preventing duplicate calls)
    lastFetchedAttributeRef: React.MutableRefObject<string | null>;
}

export function useReferenceAttributeLoader(
    options: UseReferenceAttributeLoaderOptions
): UseReferenceAttributeLoaderResult {
    const {
        isRequestApprovalType,
        open,
        possessiveTargetAttribute,
        onActorsAttributeUpdate,
        existingActorsValue
    } = options;

    // State for all reference attributes
    const [allReferenceAttributes, setAllReferenceAttributes] = useState<ReferenceAttribute[]>([]);
    const [loadingAllReferenceAttributes, setLoadingAllReferenceAttributes] = useState(false);

    // State for target entity type reference attributes
    const [targetEntityTypeReferenceAttributes, setTargetEntityTypeReferenceAttributes] = useState<any[]>([]);
    const [loadingReferenceAttributes, setLoadingReferenceAttributes] = useState(false);
    const [selectedReferenceAttributeInfo, setSelectedReferenceAttributeInfo] = useState<ReferenceAttributeInfo | null>(null);

    // Refs for caching and deduplication
    const lastFetchedAttributeRef = useRef<string | null>(null);
    const failedAttributesRef = useRef<Set<string>>(new Set());
    const onActorsAttributeUpdateRef = useRef(onActorsAttributeUpdate);
    const existingActorsValueRef = useRef(existingActorsValue);
    onActorsAttributeUpdateRef.current = onActorsAttributeUpdate;
    existingActorsValueRef.current = existingActorsValue;

    // Normalize to a stable string so object reference changes don't retrigger the effect
    const possessiveTargetAttributeKey = (() => {
        const v = possessiveTargetAttribute;
        if (v == null || v === '') return '';
        if (typeof v === 'string') return v.trim().toLowerCase();
        if (typeof v === 'object' && v !== null) {
            const id = (v as any).name ?? (v as any).Name ?? (v as any).id ?? (v as any).Id ?? (v as any).value ?? (v as any).Value ?? '';
            return String(id).trim().toLowerCase();
        }
        return String(v).trim();
    })();

    // Load all reference attributes when dialog opens (for RequestApprovalType PossessiveActorsAttribute flow).
    useEffect(() => {
        if (!isRequestApprovalType || !open) {
            return;
        }

        const loadAllReferenceAttributes = async () => {
            setLoadingAllReferenceAttributes(true);
            try {
                // 1. Try tier2 entitlements for RequestApprovalType
                const tier2Response = await EntitlementsService.fetchEntityEntitlementForSchemaEntity(REQUEST_APPROVAL_TYPE_ENTITY_ID);
                const tier2Attrs = tier2Response?.Attributes ?? [];
                const referenceAttrsFromTier2: ReferenceAttribute[] = tier2Attrs
                    .filter((attr: any) => attr.IsReference === true)
                    .map((attr: any) => {
                        const id = (attr.Id ?? attr.DisplayName ?? '').toString().trim();
                        const name = id || (attr.DisplayName ?? '').toString().trim();
                        return {
                            name: name || id,
                            id: id || name,
                            value: id || name,
                            label: (attr.DisplayName ?? name ?? id).toString(),
                            type: (attr.Type ?? 'string').toString(),
                            required: false,
                            multiValued: false,
                            isReference: true,
                            entities: (attr.Entities ?? []).map((e: any) => e?.DisplayName ?? e?.Id ?? e).filter(Boolean),
                            isReferenceTo: Array.isArray(attr.IsReferenceTo) ? attr.IsReferenceTo : []
                        };
                    })
                    .filter((a) => a.name || a.id);

                if (referenceAttrsFromTier2.length > 0) {
                    setAllReferenceAttributes(referenceAttrsFromTier2);
                    return;
                }

                // 2. Fallback: schema (returns empty when disabled)
                const scimSchemaService = new ScimSchemaService();
                const schemaAttributesResponse = await scimSchemaService.getSchemaAttributes();
                const allSchemaAttributes = schemaAttributesResponse.Resources || [];

                const referenceAttrsFromSchema: ReferenceAttribute[] = allSchemaAttributes
                    .filter((attr: any) => attr.IsReference === true)
                    .map((attr: any) => {
                        const entityNames = (attr.Entities || []).map((ent: any) =>
                            ent.DisplayName || ent.Id || ent
                        ).filter(Boolean);

                        return {
                            name: attr.DisplayName || attr.Id || '',
                            id: attr.Id || attr.DisplayName || '',
                            value: attr.Id || attr.DisplayName || '',
                            label: attr.DisplayName || attr.Id || '',
                            type: attr.Type || 'string',
                            required: attr.Required || false,
                            multiValued: attr.Array || false,
                            isReference: true,
                            entities: entityNames,
                            isReferenceTo: attr.IsReferenceTo || []
                        };
                    })
                    .filter((attr: ReferenceAttribute) => attr.name);

                if (referenceAttrsFromSchema.length > 0) {
                    setAllReferenceAttributes(referenceAttrsFromSchema);
                    return;
                }

                // 3. Fallback: Redux store field options
                try {
                    const state = store.getState();
                    const referenceAttrs = state.schema.fieldOptions
                        .filter((attr: any) => attr.isReference === true)
                        .map((attr: any) => ({
                            name: attr.name || '',
                            id: attr.id || attr.value || '',
                            value: attr.value || attr.id || '',
                            label: attr.label || attr.name || '',
                            type: attr.type || 'string',
                            required: attr.required || false,
                            multiValued: attr.multiValued || false,
                            isReference: true,
                            entities: attr.entities || [],
                            isReferenceTo: attr.isReferenceTo || []
                        }))
                        .filter((a: ReferenceAttribute) => a.name);
                    setAllReferenceAttributes(referenceAttrs);
                } catch (fallbackErr) {
                    console.error('Selector entitlement (all reference attributes, Redux fallback) failed for RequestApprovalType:', fallbackErr);
                    setAllReferenceAttributes([]);
                }
            } catch (err) {
                console.error('Selector entitlement (all reference attributes) failed for RequestApprovalType:', err);
                setAllReferenceAttributes([]);
            } finally {
                setLoadingAllReferenceAttributes(false);
            }
        };

        loadAllReferenceAttributes();
    }, [isRequestApprovalType, open]);

    // Load reference attributes for PossessiveActorsAttribute when PossessiveTargetAttribute changes (RequestApprovalType only)
    useEffect(() => {
        if (!isRequestApprovalType || !possessiveTargetAttributeKey) {
            setTargetEntityTypeReferenceAttributes([]);
            setSelectedReferenceAttributeInfo(null);
            lastFetchedAttributeRef.current = null;
            return;
        }

        const loadReferenceAttributes = async () => {
            // Extract the actual attribute name from the raw value (for API and display; preserve case)
            const targetValue = possessiveTargetAttribute;
            let attributeName: string = '';
            if (typeof targetValue === 'string') {
                attributeName = targetValue.trim();
            } else if (targetValue && typeof targetValue === 'object' && targetValue !== null) {
                attributeName = (targetValue.name || targetValue.Name || targetValue.id || targetValue.Id || targetValue.value || targetValue.Value || '').toString().trim();
            } else if (targetValue) {
                attributeName = String(targetValue).trim();
            }
            attributeName = (attributeName ?? '').toString().trim();

            if (!attributeName || attributeName === '[object Object]' || attributeName === '') {
                console.warn('⚠️ useReferenceAttributeLoader: Invalid attribute name:', targetValue, 'extracted:', attributeName);
                setTargetEntityTypeReferenceAttributes([]);
                setSelectedReferenceAttributeInfo(null);
                lastFetchedAttributeRef.current = null;
                setLoadingReferenceAttributes(false);
                return;
            }

            // Use attribute name as-is (no stripping or adding $. prefix)
            const attributeNameAsIs = (attributeName ?? '').toString().trim();

            // Prevent duplicate API calls for the same attribute
            if (lastFetchedAttributeRef.current === attributeNameAsIs) {
                setLoadingReferenceAttributes(false);
                return;
            }

            // Don't retry if this attribute previously failed
            if (failedAttributesRef.current.has(attributeNameAsIs)) {
                console.warn(`⚠️ useReferenceAttributeLoader: Skipping previously failed attribute ${attributeNameAsIs}`);
                setTargetEntityTypeReferenceAttributes([]);
                setSelectedReferenceAttributeInfo(null);
                setLoadingReferenceAttributes(false);
                return;
            }

            // Clear failed status if this is a new attribute (user changed selection)
            if (lastFetchedAttributeRef.current !== attributeNameAsIs && lastFetchedAttributeRef.current !== null) {
                failedAttributesRef.current.delete(attributeNameAsIs);
            }

            lastFetchedAttributeRef.current = attributeNameAsIs;
            setLoadingReferenceAttributes(true);

            try {
                // Use ScimSchemaService to fetch reference attributes
                const schemaService = new ScimSchemaService();
                const referenceAttrs = await schemaService.getReferenceAttributesForAttribute(
                    attributeNameAsIs,
                    'isreference eq true'
                );

                setTargetEntityTypeReferenceAttributes(referenceAttrs);

                // Also store the selected attribute info for display purposes
                if (referenceAttrs.length > 0) {
                    setSelectedReferenceAttributeInfo({
                        name: attributeNameAsIs,
                        isReference: true
                    });

                    // After loading reference attributes, try to match and restore PossessiveActorsAttribute if it exists
                    const currentExisting = existingActorsValueRef.current;
                    const currentOnUpdate = onActorsAttributeUpdateRef.current;
                    if (currentExisting && currentOnUpdate) {
                        const actorsValueStr = (typeof currentExisting === 'string' ? currentExisting : String(currentExisting)).trim();

                        // Try to find a match (ids as-is, no stripping)
                        const matchingActorsAttr = referenceAttrs.find((attr: any) => {
                            const attrId = (attr.id || attr.value || '').toString().trim();
                            const attrName = (attr.name || attr.label || attr.DisplayName || '').toString().trim();
                            return attrId === actorsValueStr ||
                                attrId.toLowerCase() === actorsValueStr.toLowerCase() ||
                                attrName === actorsValueStr;
                        });

                        if (matchingActorsAttr) {
                            const matchedId = matchingActorsAttr.id || matchingActorsAttr.value || '';
                            if (currentExisting !== matchedId) {
                                currentOnUpdate(matchedId);
                            }
                        }
                    }
                } else {
                    setSelectedReferenceAttributeInfo(null);
                }
            } catch (err) {
                console.error(`Selector data (reference attributes for attribute) failed for property "${attributeNameAsIs}":`, err);
                // Mark this attribute as failed to prevent infinite retries
                failedAttributesRef.current.add(attributeNameAsIs);
                setTargetEntityTypeReferenceAttributes([]);
                setSelectedReferenceAttributeInfo(null);
            } finally {
                setLoadingReferenceAttributes(false);
            }
        };

        loadReferenceAttributes();
    }, [possessiveTargetAttributeKey, isRequestApprovalType]);

    return {
        allReferenceAttributes,
        loadingAllReferenceAttributes,
        targetEntityTypeReferenceAttributes,
        loadingReferenceAttributes,
        selectedReferenceAttributeInfo,
        lastFetchedAttributeRef
    };
}
